// The names and unit numbers drawn over the map, their tooltip, and the click that picks a place.
// Settings: labels.<category> (off / icons / text / both), labels.unitNumbers and labels.unitBadges. Control: map.pick.
import { html } from '../dom.js';
import { placeForKey, placeLevel, unitOf, PLACES, CATEGORIES } from '../content.js';
import { placeIcon } from './icons.js';
import { labelMode, labelParts, guestsText } from './layout.js';

// What labels may not cover: every panel of the interface that is on the page (WS-4 de-cluttering), as one selector:
// one lookup per pass (was one per panel). A hidden panel has no box (display: none), so it is left out by its size.
const CHROME = '.brand, .seasons, #explore, #dock, .controls, .top-actions, #card.open, #m-explore, #toast.show, #tour-text.show, #lobby, #trees-pop, #aurora-pop, #credit-pop, .credit-chip, #tours-pop, #layers-pop, #aurora-guide.on, #feature-card.open, .cf-pin i, .mk-here span, #ph-rail, #ph-side, #ph-tour, .wf-box'; // VIS-230: a phone's column, its side row, the tour caption and "Where to?"; WS-47: the phase caption; WS-52: the card and the pins of the client's layers, and the "you are here" words (js/lazy/): a place's label gives way to them
// While a tour plays (body[data-tour], chrome/tourscreen.js) the rest has stepped aside, still with its boxes: the
// labels give way only to what stays (owner's pick Q5 A, 2026-10-31)
const TOURING = '#card.open, #lobby, #toast.show, #tour-text.show, .tour-say:not([hidden]), #ph-tour:not([hidden]), .tour-line, .tour-ctl';
const hit = (a, b) => a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];

export function mapLabels(c) {
  const { api, T, tx, i18n } = c;
  const layer = document.querySelector('#labels'), labels = [];
  // WS-15: how much other labels fade while a place is selected (0 = none .. 1 = hidden); default = original look.
  const labelDim = Math.min(1, Math.max(0, api.resort.render?.selectionFocus?.dim ?? 0.58));
  const picks = () => c.editing || (c.slots.map || []).includes('map.pick'); // WS-51: the editor picks a place whatever the preset shows
  let selected = null, hovered = null, declutterT = 1;
  const live = []; // frame(): the labels drawn this frame
  const proj = api.places.values().next().value?.anchor?.clone?.() ?? null; // WS-77: frame()'s scratch vector (a Vector3 like every anchor)

  function tip(p) {
    const tooltip = document.querySelector('#tooltip');
    if (!p || !picks()) { tooltip.hidden = true; return; }
    tooltip.innerHTML = html`<b>${c.title(p)}</b><small>${p.unit ? T('tip.unit', { size: tx(p.size), guests: guestsText(p, '', i18n) }) : tx(CATEGORIES.find((x) => x.id === p.category)?.label)}</small>`;
    tooltip.hidden = false;
  }
  function moveTooltip(x, y) { document.querySelector('#tooltip').style.transform = `translate(${x + 16}px, ${y + 14}px)`; }

  // WS-55: one label for every place of the scene, drawn or not by its level (content.js placeLevel): a place the
  // editor turns into a marked one, or brings back from hidden, gets its label without a new page
  // (WS-85: as a function, so a place the editor adds later, a ride set on a path, gets its label: `draw` below)
  function addLabel(key, o) {
    const u = unitOf(key), unit = !!u, minor = !unit && !!PLACES[key]?.minor;
    const el = document.createElement('button');
    el.className = unit ? `label unit u-${u.type}` : `label place${minor ? ' minor' : ''}`;
    el.dataset.key = key; // VIS-227: Fine-tune's Buildings tool knows which place's label was pressed (lazy/finetune.js tap)
    el.addEventListener('click', (e) => { if (api.press?.(key, e)) return; api.tours.stop(); api.select(key, true); }); // WS-51 api.press: editor mode took the press
    el.oncontextmenu = (e) => api.press?.(key, e) && e.preventDefault(); // places-menu fix: the editor's place menu opens on a right press
    el.addEventListener('pointerenter', () => { tip(placeForKey(key)); const r = el.getBoundingClientRect(); moveTooltip(r.right, r.top); });
    el.addEventListener('pointerleave', () => tip(null));
    el.style.display = 'none'; // WS-4 fix: start hidden; labels never in view used to sit at (0,0) top-left
    layer.appendChild(el);
    // WS-4: priority for de-cluttering: named places beat minor places beat unit numbers.
    labels.push({ el, key, unit, minor, category: unit ? 'stay' : PLACES[key]?.category, pos: o.anchor, prio: unit ? 1 : minor ? 2 : 3, off: false, op: -1, culled: false, shown: false, fresh: false, sx: 0, sy: 0, w: 0, h: 0 });
  }
  for (const [key, o] of api.places) addLabel(key, o);
  // ---- WS-85 (begin): the places of the scene now (the editor's frame adds and takes away a ride set on a path:
  // main.js LZ.pathPins): a new one gets its label, a gone one loses it, a pin laid again follows its new anchor
  function follow() {
    if (labels.length === api.places.size && labels.every((l) => api.places.get(l.key)?.anchor === l.pos)) return;
    for (let i = labels.length - 1; i >= 0; i--) {
      const l = labels[i], o = api.places.get(l.key);
      if (!o) { l.el.remove(); labels.splice(i, 1); if (hovered === l.key) hovered = null; }
      else l.pos = o.anchor;
    }
    const have = new Set(labels.map((l) => l.key));
    for (const [key, o] of api.places) if (!have.has(key)) addLabel(key, o);
  }
  // ---- WS-85 (end)

  /** Writes every label for the language and the label settings of the moment. */
  function draw() {
    follow(); // WS-85
    const values = c.v() || {}, units = c.badges();
    for (const l of labels) {
      const p = placeForKey(l.key);
      if (!l.unit) { // WS-55: the content of the moment (a text edited, a place brought back) says its group and rank
        const minor = !!PLACES[l.key]?.minor;
        l.category = p?.category; l.prio = minor ? 2 : 3;
        if (minor !== l.minor) { l.minor = minor; l.el.classList.toggle('minor', minor); }
      }
      const m = labelParts(l.unit ? (units ? 'both' : 'off') : labelMode(values, l.category));
      l.off = !p || !m.shown || placeLevel(l.key, values) !== 'marked'; // WS-55: only a marked place has a label
      const name = tx(PLACES[l.key]?.label || PLACES[l.key]?.title);
      l.el.innerHTML = html`${l.unit ? p?.number : html`${m.icon ? placeIcon(PLACES[l.key], l.category) : ''}${m.text ? html`<span>${name}</span>` : ''}`}`; // WS-55: a place marked by a preset without a short label shows its title
      if (!l.unit) { l.el.classList.toggle('icons', !m.text); l.el.classList.toggle('text', !m.icon); l.el.ariaLabel = m.text ? null : name; } // VIS-128: an icon alone still says the place's name
      l.w = 0;
    }
    layer.inert = !picks(); // map.pick hidden: labels are still drawn, but nothing on them can be pressed
    if (!picks()) { hovered = null; tip(null); }
  }

  // WS-4 label de-cluttering: every ~120 ms, labels are placed greedily by priority (selected/hovered
  // first, then named places, minor places, unit numbers; nearer wins ties). A label that would overlap
  // one already placed, or sit under the interface (panels, dock, controls), fades out.
  // WS-77: the interface's rectangles are read at the start of the frame, before this frame writes any label (a read
  // after the writes made the browser lay the page out again in the middle of the frame); they do not depend on the
  // labels, so the result is the same
  function chromeBlocks() {
    const blocks = [];
    for (const el of document.querySelectorAll(document.body?.dataset.tour ? TOURING : CHROME)) {
      const r = el.getBoundingClientRect();
      if (r.width && r.height) blocks.push([r.left - 4, r.top - 4, r.right + 4, r.bottom + 4]);
    }
    return blocks;
  }
  function declutter(list, blocks) {
    list.sort((a, b) => b.rank - a.rank);
    const placed = [];
    for (const l of list) {
      if (!l.w) ({ width: l.w, height: l.h } = l.el.getBoundingClientRect()); // as drawn: --pin-scale included
      const pad = l.culled ? 3 : -1; // hysteresis: a hidden label needs a little more room to come back
      const r = [l.sx - l.w / 2 - pad, l.sy - l.h - pad - (l.unit ? 4 : 10), l.sx + l.w / 2 + pad, l.sy + pad];
      const offscreen = r[0] < 2 || r[1] < 2 || r[2] > innerWidth - 2 || r[3] > innerHeight - 2;
      const blocked = !l.active && (offscreen || blocks.some((b) => hit(r, b)) || placed.some((b) => hit(r, b)));
      l.culled = blocked;
      if (!blocked) placed.push(r);
    }
  }

  return {
    draw, moveTooltip,
    /** VIS-73 (bridge `locate`): whether a place's label is drawn now and where (CSS px of the page), from this frame's own numbers (no layout read). */
    labelOf(key) {
      const l = labels.find((x) => x.key === key);
      const drawn = !!l && l.shown && !l.culled && l.op > 0;
      return { drawn, rect: drawn && l.w ? { x: Math.round(l.sx - l.w / 2), y: Math.round(l.sy - l.h), w: Math.round(l.w), h: Math.round(l.h) } : null };
    },
    hover(p) { hovered = picks() ? p?.id || null : null; tip(p); },
    select(key) { selected = key; declutterT = 1; },
    frame(camera, controls, dt) {
      const dist = camera.position.distanceTo(controls.target);
      const w = innerWidth, h = innerHeight;
      const showUnits = dist < 330;
      live.length = 0; // VIS-271: one kept list (was a new array every frame)
      declutterT += dt; // WS-77: decided first, so its layout reads come before this frame's writes
      const blocks = declutterT > 0.12 ? chromeBlocks() : null;
      for (const l of labels) {
        const p = proj.copy(l.pos).project(camera); // WS-77: one scratch vector (was a clone per label and frame)
        // a hovered building's label wears the look of a hovered label (.hover = :hover in the CSS), not the selected one's
        const picked = l.key === selected || !!c.flash?.keys.has(l.key), hov = !picked && l.key === hovered, active = picked || hov;
        const visible = !l.off && p.z < 1 && Math.abs(p.x) < 1.15 && Math.abs(p.y) < 1.15 && (l.unit ? showUnits || active : !l.minor || dist < 420 || active);
        if (!visible) { if (l.shown) { l.el.style.display = 'none'; l.shown = false; l.culled = false; l.op = -1; } continue; }
        // a label coming into view starts see-through and waits for the next de-cluttering (the next frame) before it
        // fades in: shown at once it skipped its fade (no transition out of display: none) and could flash on top of
        // another label for up to 120 ms before that pass hid it
        if (!l.shown) { l.el.style.display = ''; l.el.style.opacity = 0; l.op = -1; l.shown = true; l.fresh = true; l.culled = !active; declutterT = 1; }
        l.sx = ((p.x + 1) / 2) * w; l.sy = ((1 - p.y) / 2) * h;
        // WS-15: hover/selection emphasis is scale(var(--ls)) AFTER the positioning, so it grows in place (style.css); so is
        // the labels' size on this screen (--pin-scale on #labels: one scale for every map style)
        if (l.sx !== l.tx || l.sy !== l.ty) { l.tx = l.sx; l.ty = l.sy; l.el.style.transform = `translate3d(${l.sx}px, ${l.sy}px, 0) translate(-50%, -100%) scale(var(--pin-scale, 1)) scale(var(--ls, 1))`; } // WS-77: written only when it moved (a still map writes nothing); VIS-271: the string is built only then
        const look = picked ? 'active' : hov ? 'hover' : '';
        if (l.look !== look) { l.look = look; l.el.classList.toggle('active', picked); l.el.classList.toggle('hover', hov); }
        l.active = active;
        l.rank = (active ? 100 : 0) + l.prio * 10 - p.z;
        live.push(l);
      }
      if (blocks) { declutterT = 0; declutter(live, blocks); }
      for (const l of live) {
        if (l.fresh) { l.fresh = false; continue; } // its first frame stays at 0, so the fade has a start
        // Unit badges and minor places fade in with zoom (no switch at their distance); other labels dim while a place
        // is selected; culled ones fade out.
        let op = l.unit ? Math.min(1, (330 - dist) / 60) : l.minor ? Math.min(1, (420 - dist) / 60) : 1;
        if (l.active) op = 1;
        else if (selected) op *= 1 - labelDim; // WS-15: resort.render.selectionFocus.dim (default 0.58 = the original 42 % opacity)
        if (l.culled) op = 0;
        op = Math.round(Math.max(0, op) * 50) / 50;
        if (op !== l.op) { l.op = op; l.el.style.opacity = op; l.el.classList.toggle('culled', op === 0); }
      }
    },
  };
}
